/**
 * Normalises API response keys from PascalCase to camelCase.
 *
 * The API sets `options.UseMemberCasing()` (Startup.cs), so Newtonsoft emits C# member names
 * verbatim — `Id`, `Name`, `ProviderType`. Changing that policy is not an option: the rest of the
 * front end already reads PascalCase from it.
 *
 * Rather than spell PascalCase through every interface, template binding and component in this
 * feature, the conversion happens once at the service boundary. Requests do not need the reverse
 * treatment — Newtonsoft matches incoming property names case-insensitively, so camelCase bodies
 * bind correctly on the way out.
 */
export function camelizeKeys<T>(value: unknown): T {
  return convert(value) as T;
}

function convert(value: unknown): unknown {
  if (Array.isArray(value)) {
    return value.map(convert);
  }

  // Date and other non-plain objects must pass through untouched; only object literals carry
  // the property names being renamed.
  if (value === null || typeof value !== 'object' || value instanceof Date) {
    return value;
  }

  const result: Record<string, unknown> = {};
  for (const [key, item] of Object.entries(value as Record<string, unknown>)) {
    result[toCamel(key)] = convert(item);
  }
  return result;
}

function toCamel(key: string): string {
  if (!key) {
    return key;
  }

  // Leave an already-camelCase key alone, and don't mangle an all-caps acronym key.
  const first = key.charAt(0);
  if (first !== first.toUpperCase()) {
    return key;
  }

  return first.toLowerCase() + key.slice(1);
}
